import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AthleteCard } from './AthleteCard';
import { InviteAthleteModal } from './InviteAthleteModal';
import { EmptyState } from '@/components/EmptyState';

// Define empty state props interface
interface EmptyStateProps {
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  action?: React.ReactNode;
}

interface AthletesTabProps {
  agentData: any;
}

interface LinkedAthlete {
  id: string;
  name: string;
  sport: string;
  avatarUrl?: string;
  location?: string;
  tier: string;
  status: string;
  totalRevenue: number;
  totalHoldings: number;
  avgBusinessImpact: number;
}

export const AthletesTab: React.FC<AthletesTabProps> = ({ agentData }) => {
  const [athletes, setAthletes] = useState<LinkedAthlete[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchLinkedAthletes = async () => {
    if (!agentData?.id) return;

    try {
      setLoading(true);
      
      // Fetch talents linked to this agent with their profile data
      const { data: linkedAthletes, error } = await supabase
        .from('talents')
        .select(`
          *,
          profile:profiles!talents_profile_id_fkey(
            display_name,
            avatar_url,
            location
          )
        `)
        .eq('agent_id', agentData.id);

      if (error) throw error;

      // Transform data to match our interface
      const transformedAthletes: LinkedAthlete[] = linkedAthletes?.map((athlete: any) => ({
        id: athlete.id,
        name: athlete.name || athlete.profile?.display_name || 'Unknown Athlete',
        sport: athlete.sport || 'General',
        avatarUrl: athlete.profile?.avatar_url,
        location: athlete.profile?.location,
        tier: athlete.tier || 'Silver',
        status: athlete.status || 'Active',
        totalRevenue: athlete.total_revenue || 0,
        totalHoldings: athlete.total_holdings || 0,
        avgBusinessImpact: athlete.avg_business_impact || 0,
      })) || [];

      setAthletes(transformedAthletes);
    } catch (error) {
      console.error('Error fetching linked athletes:', error);
      toast({
        title: "Error",
        description: "Failed to load linked athletes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedAthletes();
  }, [agentData?.id]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading athletes...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <CardTitle>My Athletes ({athletes.length})</CardTitle>
        </div>
        <InviteAthleteModal>
          <Button variant="outline" size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Athlete
          </Button>
        </InviteAthleteModal>
      </CardHeader>
      
      <CardContent>
        {athletes.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No linked athletes</h3>
            <p className="text-muted-foreground mb-4">Invite athletes to link their accounts and view their performance data.</p>
            <InviteAthleteModal>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Your First Athlete
              </Button>
            </InviteAthleteModal>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {athletes.map((athlete) => (
              <AthleteCard
                key={athlete.id}
                athlete={athlete}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};